import timezone from '@/common/timezone';
import EntityModel from '@/common/models/entity-model';
import filterDelimiters from '@/components/query/adapters/filter/filter-delimiters';
import TransactionFields from '@/modules/data-exports/definitions/fields/transaction-fields';
import SubscriptionFields from '@/modules/data-exports/definitions/fields/subscription-fields';
import CustomerFields from '@/modules/data-exports/definitions/fields/customer-fields';
import _ from '@/common/helpers';

const ExportSchedules = {
    daily: 'DAILY',
    weekly: 'WEEKLY',
    monthly: 'MONTHLY',
};

export class DateRangeModel extends EntityModel {
    static DateRanges = {
        all: 'all',
        yesterday: 'yesterday',
        previousWeek: 'previous-week',
        previousMonth: 'previous-month',
        previousYear: 'previous-year',
        currentWeek: 'current-week',
        currentMonth: 'current-month',
        currentYear: 'current-year',
        custom: 'custom',
    };

    static Periods = {
        day: 'day',
        week: 'week',
        month: 'month',
        year: 'year',
    };

    static Payloads = {
        [DateRangeModel.DateRanges.all]: null,
        [DateRangeModel.DateRanges.yesterday]: {
            start: 'yesterday',
            end: 'today',
        },
        [DateRangeModel.DateRanges.previousWeek]: {
            start: '2 weeks ago sunday',
            end: 'last week sunday',
        },
        [DateRangeModel.DateRanges.previousMonth]: {
            start: 'first day of last month',
            end: 'first day of this month',
        },
        [DateRangeModel.DateRanges.previousYear]: {
            start: 'last year jan 1',
            end: 'this year jan 1',
        },
        [DateRangeModel.DateRanges.currentWeek]: {
            start: 'last sunday',
            end: 'now',
        },
        [DateRangeModel.DateRanges.currentMonth]: {
            start: 'first day of this month',
            end: 'now',
        },
        [DateRangeModel.DateRanges.currentYear]: {
            start: 'this year jan 1',
            end: 'now',
        },
    };

    static makeCustomPayload = (
        {
            periodLength = 1,
            period = DateRangeModel.Periods.day,
        },
    ) => (
        {
            start: `${periodLength} ${period} ago`,
            end: 'now',
        });

    static parsePayloadName = ({start, end} = {}) => {
        if (!start && !end) {
            return DateRangeModel.DateRanges.all;
        }

        const payload = Object.entries(DateRangeModel.Payloads)
            .filter(item => item[0] !== DateRangeModel.DateRanges.all)
            .find(item => item[1].start === start && item[1].end === end);

        // returns payload name
        return payload
            ? payload[0]
            : DateRangeModel.DateRanges.custom;
    };

    static parseCustomPayload = ({start}) => {
        const [periodLength, period] = start.split(' ');
        return {
            periodLength,
            period,
        };
    };

    static makePayLoad({name, ...customParams}) {
        if (name === DateRangeModel.DateRanges.custom) {
            return DateRangeModel.makeCustomPayload({
                periodLength: customParams.periodLength,
                period: customParams.period,
            });
        }
        return DateRangeModel.Payloads[name];
    }

    hydrate({start = null, end = null, field = null}) {
        this.start = start;
        this.end = end;
        this.field = field;
    }

    getPayload() {
        return {
            start: this.start,
            end: this.end,
            field: this.field,
        };
    }
}

export class RecurringModel extends EntityModel {
    static makeBaseInstruction = option => `FREQ=${option}`;

    static parse = (instruction, time) => {
        let selectedDay = null;
        let dayOfMonth = null;
        let scheduleOption = instruction.substring(5, instruction.indexOf(';'));

        if (scheduleOption === ExportSchedules.weekly) {
            selectedDay = instruction.substring(instruction.indexOf('BYDAY') + 6);
        } else if (scheduleOption === ExportSchedules.monthly) {
            dayOfMonth = instruction.substring(instruction.indexOf('BYMONTHDAY') + 11);
        } else if (instruction.includes(
            RecurringModel.makeBaseInstruction(ExportSchedules.daily),
        )) {
            scheduleOption = ExportSchedules.daily;
        } else {
            scheduleOption = '';
        }
        const scheduledStartTime = timezone.readUTC(time);

        return {
            selectedDay,
            dayOfMonth,
            scheduleOption,
            scheduledStartTime,
        };
    };

    static makeInstruction = (
        currentParsedData = {},
        {scheduleOption, selectedDay, dayOfMonth},
    ) => {
        const option = scheduleOption || currentParsedData.scheduleOption;
        const day = selectedDay || currentParsedData.selectedDay;
        const dayMonth = dayOfMonth || currentParsedData.dayOfMonth;
        const baseInstruction = RecurringModel.makeBaseInstruction(option);

        if (option === ExportSchedules.weekly) {
            return `${baseInstruction};BYDAY=${day}`;
        } if (option === ExportSchedules.monthly) {
            return `${baseInstruction};BYMONTHDAY=${dayMonth}`;
        }
        return baseInstruction;
    };

    hydrate({instruction = '', start = ''} = {}) {
        this.instruction = instruction
            || RecurringModel.makeInstruction({}, {
                scheduleOption: ExportSchedules.monthly,
                dayOfMonth: 1,
            });
        this.start = start || timezone.now();
    }

    getPayload() {
        return {
            instruction: this.instruction,
            start: this.start,
        };
    }
}

export class DataExportModel extends EntityModel {
    static Statuses = {
        queued: 'queued',
        expired: 'expired',
        pending: 'pending',
        completed: 'completed',
        failed: 'failed',
        processing: 'processing',
        purged: 'purged',
    };

    static Resources = {
        customers: 'customers',
        // orders: 'orders',
        subscriptions: 'subscriptions',
        transactions: 'transactions',
    };

    static Formats = {
        csv: 'csv',
        json: 'json',
        xml: 'xml',
        jsonApi: 'json-api',
    };

    static Fields = {
        [DataExportModel.Resources.customers]: CustomerFields.fields,
        [DataExportModel.Resources.subscriptions]: SubscriptionFields.fields,
        [DataExportModel.Resources.transactions]: TransactionFields.fields,
    };

    static CustomFieldsSchemas = {
        [DataExportModel.Resources.customers]: CustomerFields.customFields,
        [DataExportModel.Resources.subscriptions]: SubscriptionFields.customFields,
        [DataExportModel.Resources.transactions]: TransactionFields.customFields,
    };

    static Schedules = ExportSchedules;

    hydrate({
        id = null,
        name = null,
        resource = null,
        format = null,
        dateRange = null,
        emailNotification = null,
        fields = null,
        recurring = null,
        status = null,
        userId = null,
        ...otherFields
    } = {}) {
        this.id = id;
        this.name = name;
        this.arguments = otherFields.arguments || null;
        this.resource = resource;
        this.format = format;
        this.dateRange = dateRange ? new DateRangeModel(dateRange) : null;
        this.emailNotification = emailNotification;
        this.fields = DataExportModel.isJSONApiFormat(format)
            ? null
            : DataExportModel.sanitizeField(fields);
        this.recurring = recurring ? new RecurringModel(recurring) : null;
        this.status = status;
        this.userId = userId;
    }

    get isPending() {
        return this.status === DataExportModel.Statuses.pending;
    }

    get isCompleted() {
        return this.status === DataExportModel.Statuses.completed;
    }

    get requiresFields() {
        return !DataExportModel.isJSONApiFormat(this.format);
    }

    static isJSONApiFormat(format) {
        return format === DataExportModel.Formats.jsonApi;
    }

    static getSignedLink(links) {
        const link = links.find(item => item.rel === 'signedLink');
        return link && link.href ? link.href : null;
    }

    static getAvailableFieldsByResource(resourceName) {
        if (!DataExportModel.Resources[resourceName]) {
            throw new Error(`Undefined resource ${resourceName}`);
        }

        const fields = DataExportModel.Fields[resourceName];
        if (fields) {
            return fields;
        }

        throw new Error(`Resource ${resourceName} has not fields`);
    }

    static getAllFieldsByResource(resourceName) {
        return DataExportModel.getAvailableFieldsByResource(resourceName)
            .map(({field}) => field);
    }

    static getDefaultFieldsByResource(resourceName) {
        return DataExportModel.getAvailableFieldsByResource(resourceName)
            .filter(({isDefault}) => isDefault).map(({field}) => field);
    }

    static getCustomFieldsSchemas(resourceName) {
        return DataExportModel.CustomFieldsSchemas[resourceName];
    }

    static sanitizeField(fields) {
        if (fields !== null) {
            return fields.map(field => field.replace(/^\//, ''));
        }
        return [];
    }

    getPayload() {
        if (this.arguments && this.arguments.filter) {
            this.arguments.filter = this.arguments.filter
                .split(filterDelimiters.filter)
                .filter(value => !_.isBlank(value.split(filterDelimiters.definition)[1]))
                .join(filterDelimiters.filter);
        }

        return {
            id: this.id,
            name: this.name,
            arguments: this.arguments,
            resource: this.resource,
            format: this.format,
            dateRange: this.dateRange ? this.dateRange.getPayload() : null,
            emailNotification: this.emailNotification,
            fields: this.requiresFields ? this.fields : null,
            recurring: this.recurring ? this.recurring.getPayload() : null,
            status: this.status,
            userId: this.userId,
        };
    }
}


export default DataExportModel;
